import { useEffect, useState } from "react";
import {
  COMPANY,
  ROUTES,
  ROUTE_ALIASES,
  applicationPages,
  applications,
  coreSolutions,
  engineeringSteps,
  productGroups,
  resources,
  routeMeta,
  solutionPages,
} from "./siteData";

const CLOUD_BASE_DISPATCH_URL =
  "https://fullenviro-inquiry-api-908b82ea9-1398463841.ap-shanghai.app.tcloudbase.com/api/inquiry/dispatch";

const routeEntries = Object.entries(ROUTES);

const routeLabels = {
  highPurity: "High-Purity Water",
  roEdi: "Compact RO-EDI",
  polishing: "Polishing & POU Filtration",
  specialty: "Specialty Membrane Separation",
  replacement: "Replacement & Retrofit",
  electronics: "Electronics & Photonics",
  pharma: "Pharmaceutical & Biotechnology",
  food: "Food & Fermentation",
  chemicals: "Specialty Chemicals & Industry",
  products: "Products",
  engineering: "Engineering Support",
  pilot: "Pilot Testing & Scale-Up",
  resources: "Resources",
  about: "About FullEnviro",
  contact: "Contact",
};

function routeFromPath(pathname) {
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  return routeEntries.find(([, path]) => path === normalized)?.[0] || ROUTE_ALIASES[normalized] || "home";
}

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function SectionHeading({ eyebrow, title, text, align = "left", action }) {
  return (
    <div className={classNames("section-heading", align === "center" && "section-heading-center")}>
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h2>{title}</h2>
        {text ? <p>{text}</p> : null}
      </div>
      {action}
    </div>
  );
}

function DataCard({ label, title, text, children, className = "" }) {
  return (
    <article className={classNames("data-card", className)}>
      {label ? <div className="card-label">{label}</div> : null}
      <h3>{title}</h3>
      {text ? <p>{text}</p> : null}
      {children}
    </article>
  );
}

function Field({
  label,
  fieldKey,
  value,
  error,
  onChange,
  type = "text",
  required = false,
  placeholder,
  options = [],
  wide = false,
}) {
  const inputClass = classNames("form-control", error && "form-control-error");
  return (
    <label className={classNames("field", (type === "textarea" || wide) && "field-wide")}>
      <span>
        {label} {required ? <b aria-hidden="true">*</b> : null}
      </span>
      {type === "textarea" ? (
        <textarea
          className={inputClass}
          rows="5"
          value={value || ""}
          onChange={(event) => onChange(fieldKey, event.target.value)}
          placeholder={placeholder}
          required={required}
          aria-invalid={Boolean(error)}
        />
      ) : type === "select" ? (
        <select
          className={inputClass}
          value={value || ""}
          onChange={(event) => onChange(fieldKey, event.target.value)}
          required={required}
          aria-invalid={Boolean(error)}
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={option.value || option} value={option.value || option}>
              {option.label || option}
            </option>
          ))}
        </select>
      ) : (
        <input
          className={inputClass}
          type={type}
          value={value || ""}
          onChange={(event) => onChange(fieldKey, event.target.value)}
          placeholder={placeholder}
          required={required}
          aria-invalid={Boolean(error)}
        />
      )}
      {error ? <small className="field-error">{error}</small> : null}
    </label>
  );
}

function ProcessVisual() {
  return (
    <div className="process-visual" aria-label="High-purity water process overview">
      <div className="process-visual-topline">
        <span>Application-led process map</span>
        <span className="live-dot">Engineering review</span>
      </div>
      <div className="process-rail">
        <div className="process-node">
          <small>01</small>
          <b>Feed review</b>
          <span>Analysis & variability</span>
        </div>
        <div className="process-link"><span /></div>
        <div className="process-node process-node-active">
          <small>02</small>
          <b>RO / EDI</b>
          <span>Primary purification</span>
        </div>
        <div className="process-link"><span /></div>
        <div className="process-node">
          <small>03</small>
          <b>Polishing</b>
          <span>Ions, TOC & gases</span>
        </div>
        <div className="process-link"><span /></div>
        <div className="process-node process-node-final">
          <small>04</small>
          <b>Point of use</b>
          <span>Final quality control</span>
        </div>
      </div>
      <div className="process-readout">
        <div><span>Quality target</span><b>Defined at point of use</b></div>
        <div><span>Design basis</span><b>Feed + duty + validation</b></div>
      </div>
      <p className="visual-note">Final specifications are confirmed against the real project data.</p>
    </div>
  );
}

export default function FullEnviroHomepageMockup() {
  const [route, setRoute] = useState(() => routeFromPath(window.location.pathname));
  const [menuOpen, setMenuOpen] = useState(false);
  const [submitState, setSubmitState] = useState({ status: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const emptyForm = {
    contactType: "project",
    category: "high-purity",
    productInterest: "",
    industry: "",
    fullName: "",
    company: "",
    country: "",
    email: "",
    whatsapp: "",
    projectStage: "",
    targetGoal: "",
    flowRate: "",
    currentModel: "",
    message: "",
    sourceCampaign: "",
    agree: false,
  };

  const [formData, setFormData] = useState(emptyForm);
  const currentMeta = routeMeta[route] || routeMeta.home;
  const canonicalUrl = `${COMPANY.domain}${ROUTES[route] || "/"}`;
  const whatsappUrl = `https://wa.me/${COMPANY.whatsappNumber}?text=${encodeURIComponent(
    "Hello FullEnviro, I would like to discuss a high-purity water or membrane project."
  )}`;

  useEffect(() => {
    const onPopState = () => {
      setRoute(routeFromPath(window.location.pathname));
      setMenuOpen(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    document.title = currentMeta.title;
    document.documentElement.lang = "en";

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        const nameMatch = selector.match(/meta\[name="(.+)"\]/);
        const propertyMatch = selector.match(/meta\[property="(.+)"\]/);
        if (nameMatch) element.setAttribute("name", nameMatch[1]);
        if (propertyMatch) element.setAttribute("property", propertyMatch[1]);
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    setMeta('meta[name="description"]', "content", currentMeta.description);
    setMeta('meta[property="og:title"]', "content", currentMeta.title);
    setMeta('meta[property="og:description"]', "content", currentMeta.description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    let schema = document.getElementById("fullenviro-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.type = "application/ld+json";
      schema.id = "fullenviro-schema";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.name,
      url: COMPANY.domain,
      email: COMPANY.email,
      address: { "@type": "PostalAddress", addressLocality: "Wuhan", addressCountry: "CN" },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales and technical support",
        telephone: COMPANY.whatsappDisplay,
        availableLanguage: ["English", "Chinese"],
      },
    });
  }, [canonicalUrl, currentMeta.description, currentMeta.title]);

  const navigate = (nextRoute) => {
    const nextPath = ROUTES[nextRoute] || "/";
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
    setRoute(nextRoute);
    setMenuOpen(false);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const openInquiry = (prefill = {}) => {
    setFormData((previous) => ({ ...previous, contactType: "project", ...prefill }));
    setSubmitState({ status: "idle", message: "" });
    navigate("contact");
  };

  const handleInput = (key, value) => {
    setFormData((previous) => ({ ...previous, [key]: value }));
    setFormErrors((previous) => ({ ...previous, [key]: undefined }));
    if (submitState.status !== "idle") setSubmitState({ status: "idle", message: "" });
  };

  const validateForm = () => {
    const errors = {};
    ["fullName", "company", "country", "email", "projectStage"].forEach((key) => {
      if (!String(formData[key] || "").trim()) errors[key] = "Required";
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }
    if (formData.contactType === "project") {
      ["category", "productInterest", "targetGoal", "message"].forEach((key) => {
        if (!String(formData[key] || "").trim()) errors[key] = "Required";
      });
    } else if (!formData.targetGoal.trim()) {
      errors.targetGoal = "Required";
    }
    if (!formData.agree) errors.agree = "Please agree before submitting";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      setSubmitState({ status: "error", message: "Please review the highlighted fields." });
      return;
    }

    const categoryLabels = {
      "high-purity": "High-Purity Water System",
      separation: "Membrane Separation Project",
      replacement: "Replacement or Retrofit",
      product: "Product Inquiry",
      support: "Commissioning or Technical Support",
    };
    const categoryLabel = categoryLabels[formData.category] || "General business inquiry";
    const inquiryType = formData.contactType === "general"
      ? "general"
      : formData.category === "support"
        ? "commissioning"
        : "technical";
    const applicationType = [categoryLabel, formData.productInterest, formData.industry]
      .filter(Boolean)
      .join(" | ");
    const message = [
      `Inquiry category: ${categoryLabel}`,
      formData.productInterest ? `Product / solution interest: ${formData.productInterest}` : "",
      formData.industry ? `Industry / application: ${formData.industry}` : "",
      formData.sourceCampaign ? `Source campaign: ${formData.sourceCampaign}` : "",
      "",
      formData.message || formData.targetGoal,
    ].filter((line, index, lines) => line || (index > 0 && lines[index - 1])).join("\n").trim();

    const payload = {
      inquiryType,
      fullName: formData.fullName,
      company: formData.company,
      country: formData.country,
      email: formData.email,
      whatsapp: formData.whatsapp,
      projectStage: formData.projectStage,
      applicationType,
      targetGoal: formData.targetGoal,
      flowRate: formData.flowRate,
      currentModel: formData.currentModel,
      message,
      businessDivision: formData.category,
      productInterest: formData.productInterest,
      industry: formData.industry,
      sourceCampaign: formData.sourceCampaign,
      submittedAt: new Date().toISOString(),
      sourcePage: ROUTES[route] || "/contact",
      language: "en",
      channelCandidates: ["email", "CRM", "WhatsApp"],
      agree: formData.agree,
    };

    setIsSubmitting(true);
    setSubmitState({ status: "idle", message: "" });
    try {
      const response = await fetch(CLOUD_BASE_DISPATCH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      let data = null;
      try { data = await response.json(); } catch { data = null; }
      if (!response.ok) {
        if (data?.errors && typeof data.errors === "object") {
          setFormErrors((previous) => ({ ...previous, ...data.errors }));
        }
        const firstError = data?.errors ? Object.values(data.errors)[0] : null;
        throw new Error(data?.message || firstError || `Request failed (${response.status})`);
      }
      setSubmitState({
        status: "success",
        message: "Thank you. Your inquiry has been received. We typically reply within one business day.",
      });
      setFormData((previous) => ({
        ...emptyForm,
        fullName: previous.fullName,
        company: previous.company,
        country: previous.country,
        email: previous.email,
        whatsapp: previous.whatsapp,
        agree: previous.agree,
      }));
      setFormErrors({});
    } catch (error) {
      setSubmitState({ status: "error", message: `Submission failed. ${error?.message || "Please email us directly."}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const PageLink = ({ to, children, className = "", onClick }) => (
    <a
      href={ROUTES[to] || "/"}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        if (onClick) onClick();
        else navigate(to);
      }}
    >
      {children}
    </a>
  );

  const PrimaryActions = ({ category = "high-purity", productInterest = "" }) => (
    <div className="button-row">
      <button className="button button-primary" type="button" onClick={() => openInquiry({ category, productInterest })}>
        Discuss your application
      </button>
      <a className="button button-secondary" href={whatsappUrl} target="_blank" rel="noreferrer">WhatsApp</a>
    </div>
  );

  const RelatedLinks = ({ routes }) => (
    <div className="related-links">
      <span>Related</span>
      {routes.map((key) => <PageLink key={key} to={key}>{routeLabels[key] || key}<b>↗</b></PageLink>)}
    </div>
  );

  const renderHome = () => (
    <>
      <main className="hero">
        <div className="page-shell hero-grid">
          <div className="hero-copy">
            <div className="eyebrow eyebrow-hero">High-purity process infrastructure</div>
            <h1>High-purity water and membrane separation for critical industrial processes.</h1>
            <p className="hero-lead">
              Engineering support for RO-EDI polishing, point-of-use filtration, specialty MF/UF separation and compatible membrane replacements—from process review to export delivery and commissioning.
            </p>
            <div className="button-row">
              <button className="button button-primary" type="button" onClick={() => openInquiry({ category: "high-purity" })}>
                Discuss your application
              </button>
              <PageLink to="highPurity" className="button button-quiet">Explore solutions <span>→</span></PageLink>
            </div>
            <div className="hero-proof">
              <div><b>01</b><span>Application and data review</span></div>
              <div><b>02</b><span>Qualified China supply</span></div>
              <div><b>03</b><span>Export and technical follow-through</span></div>
            </div>
          </div>
          <ProcessVisual />
        </div>
      </main>

      <section className="signal-strip">
        <div className="page-shell signal-grid">
          <span>Built for technical buyers</span>
          <b>Electronics & photonics</b>
          <b>Pharma & biotechnology</b>
          <b>Food & fermentation</b>
          <b>Specialty chemicals</b>
        </div>
      </section>

      <section className="section page-shell">
        <SectionHeading
          eyebrow="Three connected capabilities"
          title="A focused portfolio—not a catalogue of unrelated equipment."
          text="The growth strategy stays close to FullEnviro's established engineering base: purify the water, separate the process stream, or review the replacement risk."
        />
        <div className="solution-grid">
          {coreSolutions.map((item) => (
            <PageLink key={item.route} to={item.route} className="solution-card">
              <div className="solution-card-top"><span>{item.number}</span><small>{item.eyebrow}</small></div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <div className="tag-row">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              <div className="card-action">View solution <b>→</b></div>
            </PageLink>
          ))}
        </div>
      </section>

      <section className="section section-dark">
        <div className="page-shell">
          <SectionHeading
            eyebrow="Applications"
            title="Start from what the process must protect."
            text="We organize products around the manufacturing duty, water-quality risk and validation path—not around a supplier's catalogue structure."
          />
          <div className="application-grid">
            {applications.map((item, index) => (
              <PageLink key={item.route} to={item.route} className="application-card">
                <span>0{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <small>{item.cue}</small>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      <section className="section page-shell engineering-section">
        <SectionHeading
          eyebrow="Engineering support"
          title="A clear path from incomplete data to controlled delivery."
          text="You do not need a finished specification before contacting us. We identify the missing information and define the next useful step."
          action={<PageLink to="engineering" className="button button-secondary">How we support projects</PageLink>}
        />
        <div className="engineering-steps">
          {engineeringSteps.map((step) => (
            <article key={step.number}><span>{step.number}</span><h3>{step.title}</h3><p>{step.text}</p></article>
          ))}
        </div>
      </section>

      <section className="section section-soft">
        <div className="page-shell proof-layout">
          <div>
            <div className="eyebrow">Technical credibility</div>
            <h2>Claims end where project validation must begin.</h2>
            <p>
              FullEnviro coordinates process review, selection, documentation and delivery with qualified manufacturing partners. Final performance is tied to confirmed feed limits, operating conditions and acceptance criteria.
            </p>
          </div>
          <div className="proof-list">
            <div><b>Compatibility review</b><span>Dimensions, duty, controls and operating risk</span></div>
            <div><b>Pilot before scale-up</b><span>Evidence for sensitive or unfamiliar process streams</span></div>
            <div><b>Documented handover</b><span>Technical files, FAT, spares and commissioning boundaries</span></div>
          </div>
        </div>
      </section>

      <section className="section page-shell">
        <SectionHeading
          eyebrow="Technical resources"
          title="Practical guidance before you request a quotation."
          action={<PageLink to="resources" className="text-link">View all resources <span>→</span></PageLink>}
        />
        <div className="resource-grid">
          {resources.slice(0, 3).map((item) => (
            <article className="resource-card" key={item.title}><small>{item.type}</small><h3>{item.title}</h3><p>{item.text}</p></article>
          ))}
        </div>
      </section>

      <section className="section page-shell">
        <div className="callout">
          <div><div className="eyebrow">Start with the data you have</div><h2>Send a water analysis, process description or current model.</h2><p>We will return a focused missing-data list and recommend the next practical step.</p></div>
          <PrimaryActions />
        </div>
      </section>
    </>
  );

  const renderSolutionPage = (key) => {
    const page = solutionPages[key];
    return (
      <>
        <section className="page-hero">
          <div className="page-shell page-hero-grid">
            <div>
              <div className="eyebrow">{page.eyebrow}</div>
              <h1>{page.title}</h1>
              <p>{page.lead}</p>
              <PrimaryActions category={page.category} productInterest={routeLabels[key]} />
            </div>
            <div className="metric-panel">
              {page.metrics.map(([label, value]) => <div key={label}><span>{label}</span><b>{value}</b></div>)}
            </div>
          </div>
        </section>
        <section className="section page-shell">
          <SectionHeading eyebrow="Scope" title="What the technical review covers" />
          <div className="two-column-grid">
            {page.sections.map(([title, text], index) => <DataCard key={title} label={`0${index + 1}`} title={title} text={text} />)}
          </div>
        </section>
        <section className="section section-soft">
          <div className="page-shell split-layout">
            <div><div className="eyebrow">Project input</div><h2>{page.checklistTitle}</h2><p>Incomplete information is acceptable. We will identify the critical gaps before recommending equipment or components.</p></div>
            <div className="check-panel"><ul className="check-list">{page.checklist.map((item) => <li key={item}>{item}</li>)}</ul></div>
          </div>
        </section>
        <section className="section page-shell">
          <div className="boundary-card"><div><span>Validation boundary</span><h2>Specific claims require specific project evidence.</h2></div><p>{page.boundary}</p></div>
          <RelatedLinks routes={page.related} />
        </section>
        <section className="section page-shell"><div className="callout"><div><div className="eyebrow">Technical inquiry</div><h2>Let us review the application before the quotation.</h2><p>Send the available data and we will define the next practical step.</p></div><PrimaryActions category={page.category} productInterest={routeLabels[key]} /></div></section>
      </>
    );
  };

  const renderApplicationPage = (key) => {
    const page = applicationPages[key];
    return (
      <>
        <section className="page-hero application-hero">
          <div className="page-shell page-hero-grid">
            <div><div className="eyebrow">{page.eyebrow}</div><h1>{page.title}</h1><p>{page.lead}</p><PrimaryActions category={page.category} productInterest={routeLabels[key]} /></div>
            <div className="application-use-panel"><small>Typical duties</small>{page.uses.map((use, index) => <div key={use}><span>0{index + 1}</span><b>{use}</b></div>)}</div>
          </div>
        </section>
        <section className="section page-shell split-layout">
          <div><div className="eyebrow">Application priorities</div><h2>Questions that shape a credible process route.</h2><p>The product list comes after the process risks, quality target and validation method are understood.</p></div>
          <div className="priority-list">{page.priorities.map((item, index) => <div key={item}><span>0{index + 1}</span><p>{item}</p></div>)}</div>
        </section>
        <section className="section section-dark"><div className="page-shell sector-process"><div><div className="eyebrow">Working model</div><h2>Process requirement → treatment duty → validation → controlled supply</h2></div><p>FullEnviro provides the technical-commercial coordination layer. Manufacturing and final product responsibility are confirmed in the project proposal.</p></div></section>
        <section className="section page-shell"><RelatedLinks routes={page.related} /><div className="callout"><div><div className="eyebrow">Discuss this application</div><h2>Share the current process and target quality.</h2><p>We will identify the minimum data needed for a useful technical review.</p></div><PrimaryActions category={page.category} productInterest={routeLabels[key]} /></div></section>
      </>
    );
  };

  const renderProducts = () => (
    <>
      <section className="page-hero compact-hero"><div className="page-shell page-hero-grid"><div><div className="eyebrow">Products & components</div><h1>Selected products supported by application review.</h1><p>We supply products within a defined duty and compatibility basis. Brand, model and manufacturer are confirmed in the quotation.</p><PrimaryActions category="product" productInterest="Product selection" /></div><div className="metric-panel"><div><span>Core families</span><b>Membranes • EDI • filtration</b></div><div><span>Selection basis</span><b>Application and operating data</b></div><div><span>Supply format</span><b>Components • modules • skids</b></div></div></div></section>
      <section className="section page-shell"><SectionHeading eyebrow="Product groups" title="A compact portfolio connected to the process." text="Specific manufacturers and models are selected after compatibility, documentation, lead time and support are reviewed." /><div className="product-group-grid">{productGroups.map((group, index) => <article key={group.title}><span>0{index + 1}</span><h2>{group.title}</h2><ul>{group.items.map((item) => <li key={item}>{item}</li>)}</ul><button type="button" className="text-button" onClick={() => openInquiry({ category: "product", productInterest: group.title })}>Request selection support →</button></article>)}</div></section>
      <section className="section section-soft"><div className="page-shell split-layout"><div><div className="eyebrow">Before quotation</div><h2>Equivalent is an engineering conclusion—not a marketing label.</h2><p>Replacement products are compared against physical fit, operating duty, materials, cleaning, controls and validation needs.</p></div><div className="check-panel"><ul className="check-list"><li>Existing model and quantity</li><li>Photos, drawings and dimensions</li><li>Feed or process conditions</li><li>Operating pressure, flux and recovery</li><li>Current issue and replacement objective</li><li>Required certificates and delivery date</li></ul></div></div></section>
    </>
  );

  const renderEngineering = () => (
    <>
      <section className="page-hero"><div className="page-shell page-hero-grid"><div><div className="eyebrow">Engineering support</div><h1>Technical coordination from process data to operating handover.</h1><p>FullEnviro translates project requirements into a selection basis, technical proposal, supply package and practical support plan.</p><PrimaryActions category="support" productInterest="Engineering and commissioning support" /></div><div className="metric-panel"><div><span>Front end</span><b>Review • selection • proposal</b></div><div><span>Supply stage</span><b>Documents • FAT • export</b></div><div><span>Handover</span><b>Commissioning • troubleshooting</b></div></div></div></section>
      <section className="section page-shell"><SectionHeading eyebrow="Project workflow" title="One technical thread across the supply process." /><div className="engineering-steps engineering-steps-large">{engineeringSteps.map((step) => <article key={step.number}><span>{step.number}</span><h3>{step.title}</h3><p>{step.text}</p></article>)}</div></section>
      <section className="section section-soft"><div className="page-shell two-column-grid"><DataCard label="Technical" title="Process and equipment review"><ul className="clean-list"><li>Feed and target-quality review</li><li>Process-route comparison</li><li>Component and compatibility review</li><li>Technical proposal and clarification</li><li>Pilot or sample-test planning</li></ul></DataCard><DataCard label="Delivery" title="Export and handover coordination"><ul className="clean-list"><li>Documentation and drawing control</li><li>FAT and inspection coordination</li><li>Packing and export communication</li><li>Remote commissioning support</li><li>Spare parts and troubleshooting</li></ul></DataCard></div></section>
      <section className="section page-shell"><div className="boundary-card"><div><span>Responsibility</span><h2>Clear scope protects both the process and the project.</h2></div><p>Design ownership, installation, commissioning, performance guarantees and local service responsibilities are stated in the proposal before order confirmation.</p></div><RelatedLinks routes={["pilot", "replacement", "contact"]} /></section>
    </>
  );

  const renderResources = () => (
    <>
      <section className="page-hero compact-hero"><div className="page-shell"><div className="eyebrow">Technical resources</div><h1>Selection guidance for real project decisions.</h1><p>Concise engineering notes designed to improve the quality of a specification, pilot or replacement review.</p></div></section>
      <section className="section page-shell"><div className="resource-grid resource-grid-large">{resources.map((item) => <article className="resource-card" key={item.title}><small>{item.type}</small><h2>{item.title}</h2><p>{item.text}</p><button type="button" className="text-button" onClick={() => openInquiry({ category: "product", productInterest: item.title })}>Request this guide →</button></article>)}</div></section>
      <section className="section section-soft"><div className="page-shell split-layout"><div><div className="eyebrow">Engineering study</div><h2>High-purity dilution water for specialty chemicals.</h2><p>An application-based review can compare double-pass RO, EDI, polishing, storage and point-of-use requirements without presenting an unverified project as a delivered case study.</p></div><div className="check-panel"><ul className="check-list"><li>Define source-water variability</li><li>Confirm contaminant limits at use</li><li>Compare process routes and recovery</li><li>Set sampling and acceptance points</li><li>State operating and validation boundaries</li></ul></div></div></section>
    </>
  );

  const renderAbout = () => (
    <>
      <section className="page-hero"><div className="page-shell page-hero-grid"><div><div className="eyebrow">About FullEnviro</div><h1>Engineering-led export support for membrane and high-purity processes.</h1><p>Based in Wuhan, FullEnviro connects overseas industrial requirements with qualified Chinese manufacturing partners, technical review and controlled delivery.</p><PrimaryActions /></div><div className="principle-panel"><span>Our position</span><h2>Technical export partner</h2><p>We are not presenting a factory catalogue. We coordinate selection, suppliers, documentation and delivery around the actual application.</p></div></div></section>
      <section className="section page-shell"><SectionHeading eyebrow="What makes the model useful" title="Technical depth where trading alone is not enough." /><div className="three-column-grid"><DataCard title="Application understanding" text="Membrane, water-quality and process data are translated into a workable supply brief." /><DataCard title="China supply coordination" text="Manufacturing partners are compared by capability, evidence, response and delivery fit." /><DataCard title="English project interface" text="Clarifications, documents, FAT, export and after-sales communication stay connected." /></div></section>
      <section className="section section-dark"><div className="page-shell sector-process"><div><div className="eyebrow">Focus</div><h2>Membrane separation, high-purity water and process filtration.</h2></div><p>Photonics and semiconductor manufacturing are target application industries where our existing high-purity and contamination-control knowledge can create value—not a claim that FullEnviro supplies core chipmaking equipment.</p></div></section>
    </>
  );

  const renderContact = () => {
    const projectInquiry = formData.contactType === "project";
    const isSeparation = formData.category === "separation";
    const isReplacement = formData.category === "replacement";
    return (
      <section className="section page-shell contact-page">
        <div className="contact-intro">
          <div className="eyebrow">Technical inquiry</div>
          <h1>Start with the information you already have.</h1>
          <p>Water analysis, process descriptions, membrane labels, drawings and incomplete specifications are all acceptable starting points.</p>
          <div className="contact-facts"><div><small>Company</small><b>{COMPANY.name}</b></div><div><small>Typical response</small><b>Within one business day</b></div><div><small>Email</small><a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></div><div><small>WhatsApp</small><a href={whatsappUrl} target="_blank" rel="noreferrer">{COMPANY.whatsappDisplay}</a></div></div>
          <div className="contact-note"><b>Sensitive technical files?</b><p>Submit the initial inquiry first. We can arrange an appropriate file-transfer route or NDA discussion in the reply.</p></div>
        </div>
        <form className="inquiry-form" onSubmit={handleSubmit} noValidate>
          <div className="form-tabs" aria-label="Inquiry type">
            <button type="button" className={formData.contactType === "project" ? "active" : ""} onClick={() => handleInput("contactType", "project")}>Project / product</button>
            <button type="button" className={formData.contactType === "general" ? "active" : ""} onClick={() => handleInput("contactType", "general")}>General cooperation</button>
          </div>
          <div className="form-grid">
            <Field label="Full name" fieldKey="fullName" value={formData.fullName} error={formErrors.fullName} onChange={handleInput} required placeholder="Your name" />
            <Field label="Company" fieldKey="company" value={formData.company} error={formErrors.company} onChange={handleInput} required placeholder="Company name" />
            <Field label="Country / region" fieldKey="country" value={formData.country} error={formErrors.country} onChange={handleInput} required placeholder="Country or region" />
            <Field label="Project stage" fieldKey="projectStage" value={formData.projectStage} error={formErrors.projectStage} onChange={handleInput} type="select" required options={["Initial research", "Technical evaluation", "RFQ / budget", "Pilot / validation", "Commercial purchase", "Replacement / urgent"]} />
            <Field label="Email" fieldKey="email" value={formData.email} error={formErrors.email} onChange={handleInput} type="email" required placeholder="name@company.com" />
            <Field label="WhatsApp / phone" fieldKey="whatsapp" value={formData.whatsapp} error={formErrors.whatsapp} onChange={handleInput} placeholder="+country code ..." />
            {projectInquiry ? <>
              <Field label="Inquiry category" fieldKey="category" value={formData.category} error={formErrors.category} onChange={handleInput} type="select" required options={[{ value: "high-purity", label: "High-Purity Water System" }, { value: "separation", label: "Membrane Separation Project" }, { value: "replacement", label: "Replacement or Retrofit" }, { value: "product", label: "Product Inquiry" }, { value: "support", label: "Commissioning or Technical Support" }]} />
              <Field label="Solution / product interest" fieldKey="productInterest" value={formData.productInterest} error={formErrors.productInterest} onChange={handleInput} required placeholder={isSeparation ? "e.g. fermentation broth clarification" : isReplacement ? "e.g. UF module replacement" : "e.g. compact RO-EDI system"} />
              <Field label="Industry / application" fieldKey="industry" value={formData.industry} error={formErrors.industry} onChange={handleInput} placeholder="e.g. photonics, pharmaceutical, food" />
              <Field label={isSeparation ? "Batch volume / flow" : "Required flow"} fieldKey="flowRate" value={formData.flowRate} error={formErrors.flowRate} onChange={handleInput} placeholder="m³/h, L/batch or daily demand" />
              <Field label={isReplacement ? "Existing brand / model" : "Target quality / process goal"} fieldKey="targetGoal" value={formData.targetGoal} error={formErrors.targetGoal} onChange={handleInput} required placeholder={isReplacement ? "Existing model and replacement objective" : "Required water quality or separation target"} />
              <Field label={isReplacement ? "Current model / dimensions" : "Reference equipment / model"} fieldKey="currentModel" value={formData.currentModel} error={formErrors.currentModel} onChange={handleInput} placeholder="Reference number, drawing or current equipment" />
              <Field label="Technical notes" fieldKey="message" value={formData.message} error={formErrors.message} onChange={handleInput} type="textarea" required placeholder="Describe the feed, application, current issue, acceptance criteria and timeline." />
            </> : <Field label="Cooperation intent / question" fieldKey="targetGoal" value={formData.targetGoal} error={formErrors.targetGoal} onChange={handleInput} type="textarea" required placeholder="Tell us what you would like to discuss." />}
          </div>
          <label className="consent-row"><input type="checkbox" checked={formData.agree} onChange={(event) => handleInput("agree", event.target.checked)} /><span>I agree that FullEnviro may use this information to evaluate the inquiry and contact me. See the <PageLink to="privacy">Privacy Policy</PageLink>.</span></label>
          {formErrors.agree ? <small className="field-error">{formErrors.agree}</small> : null}
          <div className="form-submit-row"><button className="button button-primary" type="submit" disabled={isSubmitting}>{isSubmitting ? "Submitting..." : "Submit inquiry"}</button><span>For drawings or reports, submit first and we will reply with an upload route.</span></div>
          {submitState.status !== "idle" ? <div className={classNames("submit-message", `submit-${submitState.status}`)} role="status">{submitState.message}</div> : null}
        </form>
      </section>
    );
  };

  const renderPrivacy = () => (
    <section className="legal-page page-shell"><div className="eyebrow">Legal</div><h1>Privacy Policy</h1><p>Last updated: August 1, 2026</p><h2>Information we collect</h2><p>We collect the business contact and project information submitted through this website, including name, company, country, email, phone and technical inquiry details.</p><h2>How we use it</h2><p>We use the information to evaluate inquiries, prepare responses, coordinate with relevant manufacturing or service partners when necessary and maintain a business follow-up record.</p><h2>Sharing and retention</h2><p>We do not sell inquiry data. Relevant technical information may be shared with selected partners only to assess feasibility or prepare a proposal. Tell us if an NDA is required before sensitive drawings or process data are shared.</p><h2>Contact</h2><p>To request correction or deletion of inquiry information, email <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p></section>
  );

  const renderTerms = () => (
    <section className="legal-page page-shell"><div className="eyebrow">Legal</div><h1>Terms & Technical Disclaimer</h1><p>Last updated: August 1, 2026</p><h2>Technical information</h2><p>Website content is provided for preliminary technical reference. Final suitability, compatibility and performance depend on confirmed specifications, operating conditions, system design and validation results.</p><h2>Samples and validation</h2><p>Samples, pilot trials or first-article inspection are recommended for custom, replacement, sensitive or performance-critical applications before commercial deployment.</p><h2>Supplier role</h2><p>FullEnviro is a technical export and engineering-support partner. Manufacturing is performed by selected manufacturing partners unless explicitly stated otherwise in a quotation or contract.</p><h2>Performance responsibility</h2><p>Design ownership, installation, commissioning, local service and performance guarantees are defined in the applicable proposal and contract.</p></section>
  );

  const pageRenderers = {
    home: renderHome,
    highPurity: () => renderSolutionPage("highPurity"),
    roEdi: () => renderSolutionPage("roEdi"),
    polishing: () => renderSolutionPage("polishing"),
    specialty: () => renderSolutionPage("specialty"),
    replacement: () => renderSolutionPage("replacement"),
    pilot: () => renderSolutionPage("pilot"),
    electronics: () => renderApplicationPage("electronics"),
    pharma: () => renderApplicationPage("pharma"),
    food: () => renderApplicationPage("food"),
    chemicals: () => renderApplicationPage("chemicals"),
    products: renderProducts,
    engineering: renderEngineering,
    resources: renderResources,
    about: renderAbout,
    contact: renderContact,
    privacy: renderPrivacy,
    terms: renderTerms,
  };

  const solutionNav = [["highPurity", "High-Purity Water"], ["roEdi", "Compact RO-EDI Systems"], ["polishing", "Polishing & POU Filtration"], ["specialty", "Specialty Membrane Separation"], ["replacement", "Replacement & Retrofit"]];
  const applicationNav = [["electronics", "Electronics & Photonics"], ["pharma", "Pharmaceutical & Biotechnology"], ["food", "Food & Fermentation"], ["chemicals", "Specialty Chemicals & Industry"]];

  return (
    <div className="site">
      <header className="site-header">
        <div className="page-shell header-inner">
          <PageLink to="home" className="brand" onClick={() => navigate("home")}>
            <span className="brand-mark"><i /><i /><i /></span>
            <span><b>FullEnviro</b><small>High-purity process solutions</small></span>
          </PageLink>
          <button className="menu-button" type="button" aria-expanded={menuOpen} aria-label="Toggle navigation" onClick={() => setMenuOpen((open) => !open)}><span /><span /><span /></button>
          <nav className={classNames("main-nav", menuOpen && "main-nav-open")} aria-label="Main navigation">
            <div className="nav-group"><span className={solutionNav.some(([key]) => route === key) ? "active" : ""}>Solutions <b>⌄</b></span><div className="nav-dropdown">{solutionNav.map(([key, label]) => <PageLink key={key} to={key} className={route === key ? "active" : ""}>{label}</PageLink>)}</div></div>
            <div className="nav-group"><span className={applicationNav.some(([key]) => route === key) ? "active" : ""}>Applications <b>⌄</b></span><div className="nav-dropdown">{applicationNav.map(([key, label]) => <PageLink key={key} to={key} className={route === key ? "active" : ""}>{label}</PageLink>)}</div></div>
            <PageLink to="products" className={route === "products" ? "active" : ""}>Products</PageLink>
            <PageLink to="engineering" className={route === "engineering" || route === "pilot" ? "active" : ""}>Engineering Support</PageLink>
            <PageLink to="resources" className={route === "resources" ? "active" : ""}>Resources</PageLink>
            <PageLink to="about" className={route === "about" ? "active" : ""}>About</PageLink>
            <PageLink to="contact" className="nav-cta">Technical Inquiry</PageLink>
          </nav>
        </div>
      </header>

      {pageRenderers[route] ? pageRenderers[route]() : renderHome()}

      <footer className="site-footer">
        <div className="page-shell footer-top">
          <div className="footer-intro"><div className="footer-brand">FullEnviro</div><p>High-purity water, membrane separation and process support for critical industrial applications.</p><span>{COMPANY.location}</span></div>
          <div><h3>Solutions</h3><PageLink to="highPurity">High-Purity Water</PageLink><PageLink to="specialty">Specialty Separation</PageLink><PageLink to="replacement">Replacement & Retrofit</PageLink><PageLink to="products">Products</PageLink></div>
          <div><h3>Applications</h3><PageLink to="electronics">Electronics & Photonics</PageLink><PageLink to="pharma">Pharma & Biotechnology</PageLink><PageLink to="food">Food & Fermentation</PageLink><PageLink to="chemicals">Specialty Chemicals</PageLink></div>
          <div><h3>Contact</h3><a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a><a href={whatsappUrl} target="_blank" rel="noreferrer">{COMPANY.whatsappDisplay}</a><PageLink to="contact">Submit technical data</PageLink></div>
        </div>
        <div className="page-shell footer-bottom"><span>© 2026 {COMPANY.name}</span><div><PageLink to="privacy">Privacy</PageLink><PageLink to="terms">Terms</PageLink></div></div>
      </footer>
    </div>
  );
}
